/**
 * Apollo Client 메인 모듈
 *
 * 모든 Apollo 관련 기능을 통합하고 export
 *
 * 구조:
 * - config.js: 환경 설정 및 상수
 * - storage.js: localStorage 키 및 유틸리티
 * - errorUtils.js: 에러 분류 및 처리
 * - tokenRefresh.js: 토큰 갱신 로직
 * - links/httpLink.js: HTTP 링크 및 인증 헤더
 * - links/errorLink.js: 에러 처리 링크
 * - wsClient.js: WebSocket 클라이언트
 */

import { ApolloClient, InMemoryCache, ApolloLink, split } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";

import { QUOTE_SUBSCRIPTION_NAMES } from './config';
import { httpLink, authLink } from './links/httpLink';
import { errorLink } from './links/errorLink';
import { createWsClient, createQuoteWsClient } from './wsClient';

// Re-export 유틸리티
export { STORAGE_KEYS, getTokenData, isTokenExpired, saveTokens, clearAuthData } from './storage';
export { setOnAuthError, classifyAuthError, isAuthError, handleAuthError } from './errorUtils';
export { ENDPOINTS, isDevelopment, apiHost, quoteHost } from './config';

// WebSocket 클라이언트 모듈 레벨 변수
let wsClient = null;
let wsLink = null;
let quoteWsClient = null;
let quoteWsLink = null;

// WebSocket 준비 상태 Promise 관리
let wsReadyPromise = null;
let wsReadyResolve = null;

// 싱글톤 상태 플래그
let isInitializing = false;
let isInitialized = false;

// Auth Link와 HTTP Link 결합
const httpLinkWithAuth = ApolloLink.from([
  errorLink,
  authLink,
  httpLink
]);

/**
 * WebSocket 준비 완료 Promise 반환
 */
export function getWebSocketReady() {
  if (!wsReadyPromise) {
    wsReadyPromise = new Promise((resolve) => {
      wsReadyResolve = resolve;
    });
  }
  return wsReadyPromise;
}

/**
 * WebSocket 연결 초기화 (로그인 성공 후 호출)
 */
export function initializeWebSocket() {
  // 싱글톤 체크: 이미 초기화 완료된 경우
  if (isInitialized && wsClient) {
    console.log("[Apollo] WebSocket 이미 초기화됨 - 기존 연결 재사용");
    return getWebSocketReady();
  }

  // 싱글톤 체크: 초기화 진행 중인 경우
  if (isInitializing) {
    console.log("[Apollo] WebSocket 초기화 진행 중 - 대기");
    return getWebSocketReady();
  }

  isInitializing = true;

  // 기존 WebSocket 클라이언트 정리
  if (wsClient) {
    wsClient.dispose();
    wsClient = null;
    wsLink = null;
  }

  if (quoteWsClient) {
    quoteWsClient.dispose();
    quoteWsClient = null;
    quoteWsLink = null;
  }

  // Promise 초기화
  if (!wsReadyPromise || !wsReadyResolve) {
    wsReadyPromise = new Promise((resolve) => {
      wsReadyResolve = resolve;
    });
  }

  // 새 WebSocket 클라이언트 생성
  wsClient = createWsClient();
  wsLink = new GraphQLWsLink(wsClient);

  // wsClient의 connected 이벤트에서 resolve 호출을 위해
  // 원래 on.connected 콜백을 래핑
  const originalOnConnected = wsClient.on;

  // Quote 서버용 WebSocket 클라이언트 생성
  quoteWsClient = createQuoteWsClient();
  quoteWsLink = new GraphQLWsLink(quoteWsClient);

  // mainServer WebSocket 연결 시 Promise resolve
  wsClient.on('connected', () => {
    if (wsReadyResolve) {
      wsReadyResolve();
      wsReadyResolve = null;
    }
  });

  // HTTP와 WebSocket 링크 분리 (중첩 split)
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      const isSubscription = definition.kind === "OperationDefinition" && definition.operation === "subscription";
      const opName = definition.name?.value;
      console.log(`[Apollo Split L1] opName: "${opName}", kind: ${definition.kind}, operation: ${definition.operation}, isSubscription: ${isSubscription}`);
      return isSubscription;
    },
    // subscription인 경우: 시세는 quoteServer, 나머지는 mainServer
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        if (definition.kind === "OperationDefinition") {
          const opName = definition.name?.value;
          const isQuote = QUOTE_SUBSCRIPTION_NAMES.includes(opName);
          console.log(`[Apollo Split] Subscription opName: "${opName}", isQuote: ${isQuote}, targets: ${QUOTE_SUBSCRIPTION_NAMES.join(', ')}`);
          return isQuote;
        }
        return false;
      },
      quoteWsLink,  // 시세 subscription → quoteServer
      wsLink        // 기타 subscription → mainServer
    ),
    httpLinkWithAuth  // Query/Mutation → HTTP
  );

  // mainClient의 link 교체
  mainClient.setLink(splitLink);

  isInitializing = false;
  isInitialized = true;

  console.log("[Apollo] WebSocket 초기화 완료 - 연결 시도 중...");

  return getWebSocketReady();
}

/**
 * WebSocket 연결 종료 (로그아웃 시 호출)
 */
export function terminateWebSocket() {
  if (wsClient) {
    wsClient.dispose();
    wsClient = null;
    wsLink = null;
  }

  if (quoteWsClient) {
    quoteWsClient.dispose();
    quoteWsClient = null;
    quoteWsLink = null;
  }

  // 싱글톤 상태 플래그 초기화
  isInitializing = false;
  isInitialized = false;
  wsReadyPromise = null;
  wsReadyResolve = null;

  // HTTP 전용으로 복귀
  mainClient.setLink(httpLinkWithAuth);
  console.log("[Apollo] WebSocket 연결 종료됨 - 상태 초기화 완료");
}

// Apollo Client 인스턴스 (HTTP 전용으로 시작)
export const mainClient = new ApolloClient({
  link: httpLinkWithAuth,
  cache: new InMemoryCache({
    typePolicies: {
      Subscription: {
        fields: {
          subscribeRequestBanking: {
            merge(existing, incoming) { return incoming; },
          },
          requestRegister: {
            merge(existing, incoming) { return incoming; },
          },
          subscribeAbuse: {
            merge(existing, incoming) { return incoming; },
          },
          subscribeCurrentState: {
            merge(existing, incoming) { return incoming; },
          },
          subscribeRealSign: {
            merge(existing, incoming) { return incoming; },
          },
          subscribeReal: {
            merge(existing, incoming) { return incoming; },
          },
          subscribeUserSetting: {
            merge(existing, incoming) { return incoming; },
          },
        },
      },
    },
  }),
});

// 기본 export
export default mainClient;
